import { useState, useRef, useEffect, useMemo, memo } from "react";
import useTranslation from "next-translate/useTranslation";
import { setSearch, usePlayers } from "../../redux/players";
import { SharedPlayer } from "@chesshotel/types";
import {
  FaSearch,
  FaChessQueen,
  FaEnvelope,
  FaUserFriends,
  FaCaretDown,
} from "react-icons/fa";
import Head from "next/head";
import ChallengePopup from "../challenge/ChallengePopup";
import Link from "next/link";
import { useRouter } from "next/router";
import { useUser } from "../../redux/user";
import { useChallenges } from "../../redux/challenges";
import { useMembership } from "@/redux/membership";
import { REGULAR_TIER, UNLIMITED_TIER } from "@chesshotel/constants";
import { cn } from "@/lib/utils";
import {
  ADS_PROVIDER_PLAYWIRE,
  ADS_PROVIDER_YANDEX,
  ADS_PROVIDER_GAM,
  useSiteData,
  ADS_PROVIDER_ADSENSE,
} from "@/redux/siteData";
import { useSettings } from "@/redux/settings";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import Avatar from "../../components/common/avatar";
import SquareMedal from "../../components/common/SquareMedal";
import { getHeighestScore } from "../../util/util";
import { Button } from "@/components/ui/button";
import AdSense from "@/components/common/ad-sense";

const LEAGUE_MODES = {
  ALL: "all" as const,
  BULLET: 3 as const,
  BLITZ: 4 as const,
  RAPID: 5 as const,
  CHESS960: 2 as const,
};

type LeagueModeType =
  | typeof LEAGUE_MODES.ALL
  | typeof LEAGUE_MODES.BULLET
  | typeof LEAGUE_MODES.BLITZ
  | typeof LEAGUE_MODES.RAPID
  | typeof LEAGUE_MODES.CHESS960;

// Helper to get league elo by mode (defined outside component to avoid recreation)
function getLeagueEloByModeStatic(
  leagueData: any,
  mode: LeagueModeType
): number | null {
  if (
    !leagueData ||
    typeof leagueData !== "object" ||
    Array.isArray(leagueData)
  ) {
    return null;
  }
  const leagueEntries: any[] = Object.values(leagueData);

  if (mode === LEAGUE_MODES.ALL) {
    const elos = leagueEntries
      .map((data) => data.elo)
      .filter((elo) => elo != null && !isNaN(Number(elo)));
    return elos.length > 0 ? Math.max(...elos) : null;
  }
  const modeData = leagueEntries.find((data) => data.modeId === mode);
  return modeData ? Number(modeData.elo) : null;
}

// Memoized Online Rank component to prevent parent rerenders
interface OnlineRankProps {
  players: SharedPlayer[];
  username: string;
  t: (key: string) => string;
}

const OnlineRank = memo(function OnlineRank({
  players,
  username,
  t,
}: OnlineRankProps) {
  const ranks = useMemo(() => {
    const modes = [
      { mode: LEAGUE_MODES.ALL, label: t("All") },
      { mode: LEAGUE_MODES.BULLET, label: "Bullet" },
      { mode: LEAGUE_MODES.BLITZ, label: "Blitz" },
      { mode: LEAGUE_MODES.RAPID, label: "Rapid" },
      { mode: LEAGUE_MODES.CHESS960, label: "Chess960" },
    ];

    return modes
      .map(({ mode, label }) => {
        // Filter players: "All" includes all non-guests, specific modes require played games
        const eligiblePlayers = players.filter((p) => {
          if (p.g) return false; // Exclude guests
          if (mode === LEAGUE_MODES.ALL) return true; // "All" includes everyone
          return getLeagueEloByModeStatic(p.leagueData, mode) !== null;
        });

        // Sort by elo (default 1200 for players without rating)
        const sortedPlayers = [...eligiblePlayers].sort((a, b) => {
          const aElo = getLeagueEloByModeStatic(a.leagueData, mode) ?? 1200;
          const bElo = getLeagueEloByModeStatic(b.leagueData, mode) ?? 1200;
          return bElo - aElo;
        });

        const rank = sortedPlayers.findIndex((p) => p.u === username) + 1;

        return { label, rank };
      })
      .filter(({ rank }) => rank > 0); // Only show modes where user has a rank
  }, [players, username, t]);

  if (ranks.length === 0) return null;

  return (
    <div>
      <div className="px-2 pb-2 mt-2 text-base font-semibold tracking-wide text-muted-foreground">
        {t("Your Online Rank")}
      </div>
      <div className="text-sm">
        {ranks.map((item, index) => (
          <div
            key={item.label}
            className={cn(
              "flex justify-between px-2 py-1.5 rounded-sm",
              index % 2 === 0 && "bg-athens-gray-100"
            )}
          >
            <span className="text-muted-foreground">{item.label}</span>
            <span className="font-semibold">{item.rank}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

function initYandex(isDesktop: boolean) {
  if (isDesktop) {
    window.yaContextCb.push(() => {
      window.Ya.Context.AdvManager.render({
        renderTo: "yandex_rtb_R-A-1674804-5",
        blockId: "R-A-1674804-5",
      });
    });
  }
  // else {
  //   window.yaContextCb.push(() => {
  //     window.Ya.Context.AdvManager.render({
  //       renderTo: "yandex_rtb_R-A-1674804-9",
  //       blockId: "R-A-1674804-7",
  //     });
  //   });
  // }
}

async function initPlayWire(isDesktop: boolean) {
  if (isDesktop) {
    if (window.ramp) {
      try {
        window.ramp.spaAds({
          ads: {
            selectorId: "players_skyscraper_right",
            type: "standard_iab_rght2",
          },
          countPageview: true,
          path: "/players"
        });
        window.ramp.triggerRefresh();
      } catch (error) {
        console.log(error);
      }
    }
  }
}

const GAM_SIZE = [[300, 600]];
// const GAM_SIZE_MOBILE = [[320, 100]];

let GAM_SLOT = "/22528994871/ch_players_right_rail";
// let GAM_SLOT_MOBILE = "/22528994871/ch_lobby_mobile_bottom";
let GAM_ID = "div-ch_players_right_rail";
// let GAM_ID_MOBILE = "div-ch_lobby_mobile_bottom";

function initGAM(slotRef: any, isDesktop: boolean) {
  if (!window.googletag?.cmd || !isDesktop) return;

  // let GAM_SLOT = GAM_SLOT_MOBILE;
  // let GAM_ID = GAM_ID_MOBILE;
  // let GAM_SIZE = GAM_SIZE_MOBILE;

  // if (isDesktop) {
  //   GAM_SLOT = GAM_SLOT_DESKTOP;
  //   GAM_ID = GAM_ID_DESKTOP;
  //   GAM_SIZE = GAM_SIZE_DESKTOP;
  // }

  window.googletag.cmd.push(() => {
    const slot = window.googletag
      .defineSlot(GAM_SLOT, GAM_SIZE, GAM_ID)
      ?.addService(window.googletag.pubads());

    if (!slot) return;
    slotRef.current = slot;

    window.googletag.display(GAM_ID);
  });
}

export default function Players() {
  const { t } = useTranslation("common");

  const { players, search } = usePlayers();
  const { username } = useUser();
  const { sentChallenges } = useChallenges();
  const { tier } = useMembership();

  const [challengeUsername, setChallengeUsername] = useState<string | null>(
    null
  );
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [activeLeagueMode, setActiveLeagueMode] = useState<LeagueModeType>(
    LEAGUE_MODES.ALL
  );

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: "name" | "rating" | "playing";
    direction: "asc" | "desc";
  } | null>(null);
  const [nrPlayers, setNrPlayers] = useState(100);
  const [challengeOpen, setChallengeOpen] = useState(false);

  const playersEl = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { adsVisible, adsProvider } = useSiteData();
  const { isDesktop } = useSettings();

  // Determine if search is active
  const isSearching = search !== "";
  const slotRef = useRef<any | null>(null);

  useEffect(() => {
    setSearch("");
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setNrPlayers(100);
  }, [search, activeLeagueMode]);

  useEffect(() => {
    if (
      !adsVisible ||
      isDesktop === null ||
      tier === REGULAR_TIER ||
      tier === UNLIMITED_TIER
    ) {
      return;
    }

    if (adsProvider === ADS_PROVIDER_PLAYWIRE) {
      initPlayWire(isDesktop);
    } else if (adsProvider === ADS_PROVIDER_YANDEX) {
      initYandex(isDesktop);
    } else if (adsProvider === ADS_PROVIDER_GAM) {
      initGAM(slotRef, isDesktop);
    }

    return () => {
      if (!window.googletag?.cmd || !slotRef.current) return;

      window.googletag.cmd.push(() => {
        window.googletag.destroySlots([slotRef.current]);
      });

      slotRef.current = null;
    };
  }, [adsVisible, adsProvider, tier, isDesktop]);

  function renderSideAdRight() {
    if (tier === REGULAR_TIER || tier === UNLIMITED_TIER) {
      return null;
    }

    let ad = null;

    switch (adsProvider) {
      case ADS_PROVIDER_PLAYWIRE:
        ad = (
          <div
            id="players_skyscraper_right"
            className="hidden md:flex flex-col items-center md:w-[300px] md:max-w-[300px]"
          />
        );
        break;

      case ADS_PROVIDER_YANDEX:
        ad = (
          <div
            className="hidden md:flex flex-col items-center md:w-[300px] md:max-w-[300px] max-h-[600px]"
            id="yandex_rtb_R-A-1674804-8"
          ></div>
        );
        break;

      case ADS_PROVIDER_ADSENSE:
        ad = (
          <AdSense
            adStyles="hidden md:flex flex-col items-center md:w-[300px] md:max-w-[300px] max-h-[600px]"
            showRemoveAds={false}
            slot="3600357831"
            format="auto"
            insStyle={{ width: "100%" }}
          />
        );
        break;

      case ADS_PROVIDER_GAM:
        ad = (
          <div
            id={GAM_ID}
            className="hidden md:flex flex-col items-center md:w-[300px] md:max-w-[300px] max-h-[600px]"
          >
            {/* DIY ad slot - implement your ad solution here */}
          </div>
        );
        break;

      default:
        ad = null;
    }
    return (
      <div className="flex flex-col p-3 rounded-md bg-athens-gray-150 justify-center items-center md:w-[332px] h-[656px]">
        {ad}
      </div>
    );
    // return (
    //   <div className="flex flex-col  items-center md:w-[332px] max-h-[632px]">
    //     {ad}
    //   </div>
    // );
  }
  // Helper functions used in sorting and filtering
  function getLeagueEloByMode(
    leagueData: any,
    mode: LeagueModeType
  ): number | null {
    if (
      !leagueData ||
      typeof leagueData !== "object" ||
      Array.isArray(leagueData)
    ) {
      return null;
    }
    const leagueEntries: any[] = Object.values(leagueData);

    if (mode === LEAGUE_MODES.ALL) {
      const elos = leagueEntries
        .map((data) => data.elo)
        .filter((elo) => elo != null && !isNaN(Number(elo)));
      return elos.length > 0 ? Math.max(...elos) : null;
    }
    const modeData = leagueEntries.find((data) => data.modeId === mode);
    return modeData ? Number(modeData.elo) : null;
  }

  function getLeagueGamesByMode(
    leagueData: any,
    mode: LeagueModeType
  ): number | null {
    if (
      !leagueData ||
      typeof leagueData !== "object" ||
      Array.isArray(leagueData)
    ) {
      return null;
    }
    const leagueEntries: any[] = Object.values(leagueData);
    const modeData = leagueEntries.find((data) => data.modeId === mode);
    if (!modeData) return null;
    const wins = modeData.wins || 0;
    const losses = modeData.losses || 0;
    const draws = modeData.draws || 0;
    const total = wins + losses + draws;
    return total > 0 ? total : null;
  }

  function getDisplayName(name: string) {
    if (!name) return "";
    return name.length >= 2 && name.substring(0, 2) === "G*"
      ? name.substring(2)
      : name;
  }

  // Filter and Sort Players
  const filteredAndSortedPlayers = useMemo(() => {
    let result = players;

    // 1. Search Filter
    if (search !== "") {
      result = result.filter((player) => {
        const name = getDisplayName(player.u);
        return name.toLowerCase().startsWith(search.toLowerCase());
      });
    }

    // 2. Filter out guests when not searching (guests only appear in search results)
    if (!isSearching) {
      result = result.filter((p) => !p.g);
    }

    // 3. League mode filter (skip when searching to search all players)
    // "All" mode shows all registered players (with 1200 default), specific modes require played games
    if (!isSearching && activeLeagueMode !== LEAGUE_MODES.ALL) {
      result = result.filter(
        (p) => getLeagueEloByMode(p.leagueData, activeLeagueMode) !== null
      );
    }

    // 4. Sorting
    if (sortConfig) {
      result = [...result].sort((a, b) => {
        if (sortConfig.key === "name") {
          const aName = getDisplayName(a.u).toLowerCase();
          const bName = getDisplayName(b.u).toLowerCase();
          return sortConfig.direction === "asc"
            ? aName.localeCompare(bName)
            : bName.localeCompare(aName);
        }
        if (sortConfig.key === "playing") {
          // Playing status: currently playing (p=true) vs not
          const aP = a.p ? 1 : 0;
          const bP = b.p ? 1 : 0;
          return sortConfig.direction === "asc" ? aP - bP : bP - aP;
        }
        if (sortConfig.key === "rating") {
          let aElo = 1200,
            bElo = 1200;
          if (isSearching) {
            // When searching, use highest available rating
            aElo =
              getLeagueEloByMode(a.leagueData, LEAGUE_MODES.ALL) ??
              a.elo ??
              1200;
            bElo =
              getLeagueEloByMode(b.leagueData, LEAGUE_MODES.ALL) ??
              b.elo ??
              1200;
          } else {
            aElo = getLeagueEloByMode(a.leagueData, activeLeagueMode) ?? 1200;
            bElo = getLeagueEloByMode(b.leagueData, activeLeagueMode) ?? 1200;
          }
          return sortConfig.direction === "asc" ? aElo - bElo : bElo - aElo;
        }
        return 0;
      });
    } else {
      // Default sort: Rating DESC (usually what users want)
      result = [...result].sort((a, b) => {
        let aElo = 1200,
          bElo = 1200;
        if (isSearching) {
          // When searching, use highest available rating
          aElo =
            getLeagueEloByMode(a.leagueData, LEAGUE_MODES.ALL) ?? a.elo ?? 1200;
          bElo =
            getLeagueEloByMode(b.leagueData, LEAGUE_MODES.ALL) ?? b.elo ?? 1200;
        } else {
          aElo = getLeagueEloByMode(a.leagueData, activeLeagueMode) ?? 1200;
          bElo = getLeagueEloByMode(b.leagueData, activeLeagueMode) ?? 1200;
        }
        return bElo - aElo;
      });
    }

    return result;
  }, [players, search, isSearching, activeLeagueMode, sortConfig]);

  // Infinite scroll logic using IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setNrPlayers((prev) => {
            if (prev < filteredAndSortedPlayers.length) {
              return prev + 100;
            }
            return prev;
          });
        }
      },
      {
        root: null,
        rootMargin: "200px", // Start loading 200px before reaching the end
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filteredAndSortedPlayers.length]);

  const visiblePlayers = filteredAndSortedPlayers.slice(0, nrPlayers);

  // Sorting Handlers
  const handleSort = (key: "name" | "rating" | "playing") => {
    setSortConfig((current) => {
      if (current?.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "desc" }; // Default to desc for new sort
    });
  };

  const getSortIcon = (key: "name" | "rating" | "playing") => {
    if (sortConfig?.key !== key) return <FaCaretDown className="opacity-20" />;
    return (
      <FaCaretDown
        className={cn(
          "transition-transform",
          sortConfig.direction === "asc" && "rotate-180"
        )}
      />
    );
  };

  const challengePlayer = (username: string, id: string) => {
    setChallengeUsername(username);
    setChallengeId(id);
    setChallengeOpen(true);
  };

  const rightHeader = (
    <div className="flex relative items-center pr-3 md:pr-0">
      <div className="absolute right-6 text-orange-400 md:right-3">
        <FaSearch />
      </div>
      <input
        className="p-2 w-40 text-sm rounded border border-input bg-background md:w-64 focus:outline-none focus:ring-1 focus:ring-ring"
        type="text"
        placeholder={t("Search player")}
        onChange={(event) => setSearch(event.target.value)}
      />
    </div>
  );

  const loggedInPlayer = players.find((p) => p.u === username);
  const bulletElo = getLeagueEloByMode(
    loggedInPlayer?.leagueData,
    LEAGUE_MODES.BULLET
  );
  const blitzElo = getLeagueEloByMode(
    loggedInPlayer?.leagueData,
    LEAGUE_MODES.BLITZ
  );
  const rapidElo = getLeagueEloByMode(
    loggedInPlayer?.leagueData,
    LEAGUE_MODES.RAPID
  );
  const chess960Elo = getLeagueEloByMode(
    loggedInPlayer?.leagueData,
    LEAGUE_MODES.CHESS960
  );

  return (
    <div className="flex justify-center">
      <Head>
        <title>{"Chess Hotel | " + t("Players")}</title>
        <meta name="description" content={t("description")} />
        <meta name="keywords" content={t("keywords")} />
      </Head>
      <div className="w-[1040px] flex flex-col">
        <div className="flex justify-between h-[80px]">
          <div className="flex gap-2 items-center pl-4 text-xl font-semibold">
            <span className="text-orange-400">
              <FaUserFriends />
            </span>
            <span>{t("Players")}</span>
          </div>
          {rightHeader}
        </div>
        <div className="flex justify-center md:gap-4">
          <div className="w-full bg-white  border border-b-[3px] md:rounded-lg mb-20 overflow-hidden">
            {challengeOpen && (
              <ChallengePopup
                friend={{ id: challengeId, username: challengeUsername }}
                onClose={() => setChallengeOpen(false)}
              />
            )}

            <div className="px-4 py-6 mx-auto md:px-4" ref={playersEl}>
              <div className="w-full">
                {/* League Mode Tabs */}
                {!isSearching && (
                  <Tabs
                    value={String(activeLeagueMode)}
                    onValueChange={(val) => {
                      if (val === "all") {
                        setActiveLeagueMode(LEAGUE_MODES.ALL);
                      } else {
                        const numVal = Number(val);
                        if (
                          numVal === LEAGUE_MODES.BULLET ||
                          numVal === LEAGUE_MODES.BLITZ ||
                          numVal === LEAGUE_MODES.RAPID ||
                          numVal === LEAGUE_MODES.CHESS960
                        ) {
                          setActiveLeagueMode(numVal as LeagueModeType);
                        }
                      }
                    }}
                    className="w-full"
                  >
                    <div className="overflow-x-auto mb-6 border-b border-border">
                      <TabsList className="flex flex-nowrap justify-start p-0 space-x-4 w-max h-auto bg-transparent rounded-none md:space-x-6">
                        <TabsTrigger
                          value={String(LEAGUE_MODES.ALL)}
                          variant="underline"
                          className="flex gap-2 items-center shrink-0 data-[state=active]:text-muted-foreground"
                        >
                          {t("All")}
                        </TabsTrigger>
                        <TabsTrigger
                          value={String(LEAGUE_MODES.BULLET)}
                          variant="underline"
                          className="flex gap-2 items-center shrink-0 data-[state=active]:text-muted-foreground"
                        >
                          Bullet
                        </TabsTrigger>
                        <TabsTrigger
                          value={String(LEAGUE_MODES.BLITZ)}
                          variant="underline"
                          className="flex gap-2 items-center shrink-0 data-[state=active]:text-muted-foreground"
                        >
                          Blitz
                        </TabsTrigger>
                        <TabsTrigger
                          value={String(LEAGUE_MODES.RAPID)}
                          variant="underline"
                          className="flex gap-2 items-center shrink-0 data-[state=active]:text-muted-foreground"
                        >
                          Rapid
                        </TabsTrigger>
                        <TabsTrigger
                          value={String(LEAGUE_MODES.CHESS960)}
                          variant="underline"
                          className="flex gap-2 items-center shrink-0 data-[state=active]:text-muted-foreground"
                        >
                          Chess960
                        </TabsTrigger>
                      </TabsList>
                    </div>
                  </Tabs>
                )}

                {/* Content */}
                <div className="min-w-0">
                  {/* Player Table */}
                  <table className="w-full text-sm text-left border-collapse">
                    <thead>
                      <tr className="">
                        <th
                          className="px-4 py-3 rounded-tl-md  font-medium cursor-pointer bg-altblue-500 text-white  hover:bg-altblue-600 transition-colors w-[50%] "
                          onClick={() => handleSort("name")}
                        >
                          <div className="flex items-center space-x-1 font-medium">
                            <span>{t("Player")}</span>
                            {getSortIcon("name")}
                          </div>
                        </th>
                        <th
                          className="px-4 py-3 font-medium cursor-pointer hover:bg-altblue-600 transition-colors w-[25%] bg-altblue-500 text-white"
                          onClick={() => handleSort("rating")}
                        >
                          <div className="flex items-center space-x-1">
                            <span>{t("Rating")}</span>
                            {getSortIcon("rating")}
                          </div>
                        </th>
                        <th
                          className="px-4 py-3 rounded-tr-md font-medium cursor-pointer hover:bg-altblue-600 transition-colors w-[25%] bg-altblue-500 text-white"
                          onClick={() => handleSort("playing")}
                        >
                          <div className="flex items-center space-x-1">
                            <span className="whitespace-nowrap">
                              {t("Invite")}
                            </span>
                            {getSortIcon("playing")}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visiblePlayers.map((player) => {
                        const isChallenged = sentChallenges.some(
                          (chall) => chall.opponentUsername === player.u
                        );
                        const isPlaying = player.p;
                        const isMe = username === player.u;
                        const isGuest = player.g === true;
                        const name = getDisplayName(player.u);

                        let displayElo;
                        if (isSearching) {
                          // When searching, show highest available rating
                          displayElo =
                            getLeagueEloByMode(
                              player.leagueData,
                              LEAGUE_MODES.ALL
                            ) ??
                            player.elo ??
                            1200;
                        } else if (activeLeagueMode === LEAGUE_MODES.ALL) {
                          // "All" mode: show highest elo or default 1200
                          displayElo =
                            getLeagueEloByMode(
                              player.leagueData,
                              LEAGUE_MODES.ALL
                            ) ?? 1200;
                        } else {
                          // Specific mode: show mode elo (shouldn't be null due to filtering)
                          displayElo =
                            getLeagueEloByMode(
                              player.leagueData,
                              activeLeagueMode
                            ) ?? "-";
                        }

                        const highestScore = getHeighestScore(player.scores);

                        return (
                          <tr
                            key={player.u}
                            className="border-b border-border font-normal text-[#555] odd:bg-[#f6f6f6] transition-colors hover:bg-muted/50"
                          >
                            <td className="px-4 py-2.5">
                              <div className="flex items-center space-x-2">
                                {isGuest ? (
                                  <Avatar
                                    id={player.id}
                                    avatarId={player.avatarId}
                                    avatarType={player.avatarType}
                                    fbId={player.fbId}
                                    googleId={player.googleId}
                                    className="w-8 h-8 md:w-8 md:h-8"
                                  />
                                ) : (
                                  <Link
                                    href={`/user?userId=${player.id}&link=players`}
                                  >
                                    <Avatar
                                      id={player.id}
                                      avatarId={player.avatarId}
                                      avatarType={player.avatarType}
                                      fbId={player.fbId}
                                      googleId={player.googleId}
                                      className="w-8 h-8 cursor-pointer md:w-8 md:h-8"
                                    />
                                  </Link>
                                )}
                                <div className="flex items-center">
                                  <div className="font-medium">
                                    {isChallenged && !isPlaying && (
                                      <span className="mr-2 text-xs text-green-500">
                                        ({t("Challenged")})
                                      </span>
                                    )}
                                    {isGuest ? (
                                      <span
                                        className={cn(
                                          isMe && "text-blue-600 font-bold"
                                        )}
                                      >
                                        {name}
                                      </span>
                                    ) : (
                                      <Link
                                        href={`/user?userId=${player.id}&link=players`}
                                        className={cn(
                                          "hover:underline cursor-pointer",
                                          isMe && "text-blue-600 font-bold"
                                        )}
                                      >
                                        {name}
                                      </Link>
                                    )}
                                  </div>
                                  {highestScore && (
                                    <div className="ml-2">
                                      <SquareMedal
                                        rank={highestScore.rank}
                                        size="small"
                                      // leagueName={highestScore.leagueName}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 text-muted-foreground">
                              {displayElo}
                            </td>
                            <td className="px-4">
                              <div className="flex items-center h-full">
                                {/* {isMe && (
                                  <span
                                    title={t("Playing")}
                                    className="pl-[13px] text-muted-foreground"
                                  >
                                    <FaChessQueen />
                                  </span>
                                )} */}

                                {!isChallenged &&
                                  !isMe &&
                                  (isPlaying ? (
                                    <span
                                      title={t("Playing")}
                                      className="pl-[13px] text-muted-foreground"
                                    >
                                      <FaChessQueen />
                                    </span>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() =>
                                        challengePlayer(
                                          player.u,
                                          String(player.id)
                                        )
                                      }
                                      className="text-sm text-blue-500 transition-colors hover:text-blue-700"
                                      title={t("Challenge")}
                                    >
                                      <FaEnvelope />
                                    </Button>
                                  ))}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {visiblePlayers.length === 0 && (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-4 py-8 text-center text-muted-foreground"
                          >
                            {t("No players found")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {/* Sentinel element for infinite scroll */}
                  <div ref={sentinelRef} className="h-4" />
                  {visiblePlayers.length < filteredAndSortedPlayers.length && (
                    <div className="py-4 text-center text-muted-foreground">
                      {t("Loading more players...")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* RIGHT COLUMN (SIDEBAR) */}
          <div className="mt-8 md:block md:mt-0">
            <div className="hidden md:block">
              {/* <div className="w-[332px] flex flex-col text-sm  mb-20"> */}

              <div className="w-[332px] flex flex-col text-sm space-y-4 mb-20">
                {/* Sidebar Content */}
                {renderSideAdRight()}
                {loggedInPlayer && !loggedInPlayer.g && (
                  <div className="flex flex-col p-3 py-3 rounded-md bg-athens-gray-150">
                    {/* Ad Placeholder or Component */}

                    {/* Your Online Rank */}
                    <OnlineRank players={players} username={username} t={t} />
                  </div>
                )}
                {loggedInPlayer &&
                  !loggedInPlayer.g &&
                  (bulletElo || blitzElo || rapidElo || chess960Elo) && (
                    <div className="flex flex-col p-3 py-3 rounded-md bg-athens-gray-150">
                      {/* User Ratings - only show if user has played at least one mode */}
                      <div>
                        <div className="px-2 pb-2 mt-2 text-base font-semibold tracking-wide text-muted-foreground">
                          {t("Your Ratings")}
                        </div>
                        <div className="text-sm">
                          {[
                            { label: "Bullet", value: bulletElo },
                            { label: "Blitz", value: blitzElo },
                            { label: "Rapid", value: rapidElo },
                            { label: "Chess960", value: chess960Elo },
                          ]
                            .filter((item) => item.value)
                            .map((item, index) => (
                              <div
                                key={item.label}
                                className={cn(
                                  "flex justify-between px-2 py-1.5 rounded-sm",
                                  index % 2 === 0 && "bg-athens-gray-100"
                                )}
                              >
                                <span className="text-muted-foreground">
                                  {item.label}
                                </span>
                                <span className="font-semibold">
                                  {item.value}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Games Played */}
                {/* {loggedInPlayer &&
              !loggedInPlayer.g &&
              (getLeagueGamesByMode(
                loggedInPlayer?.leagueData,
                LEAGUE_MODES.BULLET
              ) ||
                getLeagueGamesByMode(
                  loggedInPlayer?.leagueData,
                  LEAGUE_MODES.BLITZ
                ) ||
                getLeagueGamesByMode(
                  loggedInPlayer?.leagueData,
                  LEAGUE_MODES.RAPID
                ) ||
                getLeagueGamesByMode(
                  loggedInPlayer?.leagueData,
                  LEAGUE_MODES.CHESS960
                )) && (
                <div>
                  <div className="px-2 pb-2 mt-2 text-base font-semibold tracking-wide text-muted-foreground">
                    {t("Games Played")}
                  </div>
                  <div className="text-sm">
                    {[
                      {
                        label: "Bullet",
                        value: getLeagueGamesByMode(
                          loggedInPlayer?.leagueData,
                          LEAGUE_MODES.BULLET
                        ),
                      },
                      {
                        label: "Blitz",
                        value: getLeagueGamesByMode(
                          loggedInPlayer?.leagueData,
                          LEAGUE_MODES.BLITZ
                        ),
                      },
                      {
                        label: "Rapid",
                        value: getLeagueGamesByMode(
                          loggedInPlayer?.leagueData,
                          LEAGUE_MODES.RAPID
                        ),
                      },
                      {
                        label: "Chess960",
                        value: getLeagueGamesByMode(
                          loggedInPlayer?.leagueData,
                          LEAGUE_MODES.CHESS960
                        ),
                      },
                    ]
                      .filter((item) => item.value)
                      .map((item, index) => (
                        <div
                          key={item.label}
                          className={cn(
                            "flex justify-between px-2 py-1.5 rounded-sm",
                            index % 2 === 0 && "bg-athens-gray-100"
                          )}
                        >
                          <span className="text-muted-foreground">
                            {item.label}
                          </span>
                          <span className="font-semibold">{item.value}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )} */}

                {loggedInPlayer && loggedInPlayer.g && (
                  <div className="flex flex-col p-3 py-3 rounded-md bg-athens-gray-150">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 dark:bg-blue-900/20 dark:border-blue-800">
                      <h3 className="font-bold text-blue-700 dark:text-blue-300">
                        {t("Guest player")}
                      </h3>
                      <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                        <Link
                          href="/register"
                          className="font-semibold underline hover:text-blue-800 dark:hover:text-blue-200"
                        >
                          {t("Register now")}
                        </Link>{" "}
                        {t("to get your ranking")}
                      </p>
                    </div>
                  </div>
                )}

                {/* Placeholder for "Top Players Today" or "Trending" */}
                {/* <div className="p-4 rounded-lg border shadow-sm bg-card">
              <h3 className="mb-3 text-sm font-semibold tracking-wide uppercase text-muted-foreground">
                Trending Players
              </h3>
              <div className="text-sm italic text-muted-foreground">
                Coming soon...
              </div>
            </div> */}
              </div>
              {/* </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
